/**
 * Contas de fuso horário sem biblioteca: só `Intl`, que já sabe as regras de
 * cada fuso IANA (inclusive horário de verão). Serve à API e ao app.
 */
export const DEFAULT_TIMEZONE = 'America/Sao_Paulo';
const DAY_MS = 24 * 60 * 60 * 1000;
const IANA_SHAPE = /^[A-Za-z][A-Za-z0-9_+-]*(\/[A-Za-z0-9_+-]+){1,2}$/;
const formatters = new Map();
function formatterOf(timeZone) {
    let formatter = formatters.get(timeZone);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat('en-US', {
            timeZone,
            hourCycle: 'h23',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
        formatters.set(timeZone, formatter);
    }
    return formatter;
}
/** Aceita nomes IANA ("America/Manaus") e "UTC"; recusa deslocamento solto ("-03:00"). */
export function isValidTimeZone(timeZone) {
    if (timeZone !== 'UTC' && !IANA_SHAPE.test(timeZone))
        return false;
    try {
        formatterOf(timeZone);
        return true;
    }
    catch {
        return false;
    }
}
/** O fuso informado, se for válido; senão, o padrão (Brasília). */
export function resolveTimeZone(timeZone) {
    return timeZone && isValidTimeZone(timeZone) ? timeZone : DEFAULT_TIMEZONE;
}
/** O que o relógio de parede mostra naquele fuso, naquele instante. */
export function toLocalDateTime(instant, timeZone) {
    const local = { year: 0, month: 0, day: 0, hour: 0, minute: 0, second: 0 };
    for (const part of formatterOf(timeZone).formatToParts(instant)) {
        if (part.type in local)
            local[part.type] = Number(part.value);
    }
    return local;
}
function asUtc(local) {
    return Date.UTC(local.year, local.month - 1, local.day, local.hour, local.minute, local.second);
}
/** Deslocamento do fuso naquele instante, em milissegundos (hora local menos UTC). */
function offsetAt(ms, timeZone) {
    const whole = Math.floor(ms / 1000) * 1000;
    return asUtc(toLocalDateTime(new Date(whole), timeZone)) - whole;
}
/**
 * Hora de parede num fuso → instante. Nas viradas de horário de verão segue o
 * padrão de calendário: hora que não existe (relógio adiantado) usa o
 * deslocamento de antes da virada, e hora repetida (relógio atrasado) fica com
 * a primeira das duas.
 */
export function localToInstant(local, timeZone) {
    const naive = asUtc(local);
    const before = offsetAt(naive - DAY_MS, timeZone);
    const after = offsetAt(naive + DAY_MS, timeZone);
    if (before === after)
        return new Date(naive - before);
    const first = naive - before;
    const second = naive - after;
    const firstValid = offsetAt(first, timeZone) === before;
    const secondValid = offsetAt(second, timeZone) === after;
    if (firstValid && secondValid)
        return new Date(Math.min(first, second));
    if (secondValid)
        return new Date(second);
    return new Date(first);
}
/** "2026-10-01" + "18:00" no fuso da pessoa → instante. Data inválida vira `Invalid Date`. */
export function localDateTimeToInstant(date, time, timeZone) {
    const [year, month, day] = date.split('-').map(Number);
    const [hour, minute] = time.split(':').map(Number);
    const naive = Date.UTC(year, month - 1, day, hour, minute, 0);
    const check = new Date(naive);
    if (Number.isNaN(naive) ||
        check.getUTCMonth() !== month - 1 ||
        check.getUTCDate() !== day ||
        hour > 23 ||
        minute > 59) {
        return new Date(Number.NaN);
    }
    return localToInstant({ year, month, day, hour, minute, second: 0 }, timeZone);
}
/** Dia do calendário ("AAAA-MM-DD") daquele instante, no fuso da pessoa. */
export function localDateOf(instant, timeZone) {
    const local = toLocalDateTime(instant, timeZone);
    return `${String(local.year).padStart(4, '0')}-${String(local.month).padStart(2, '0')}-${String(local.day).padStart(2, '0')}`;
}
/** Meia-noite (começo do dia) de uma data do calendário, no fuso da pessoa. */
export function startOfLocalDay(date, timeZone) {
    return localDateTimeToInstant(date, '00:00', timeZone);
}
/** Soma dias a uma data do calendário ("AAAA-MM-DD"), sem passar por fuso. */
export function addDaysToLocalDate(date, days) {
    const [year, month, day] = date.split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10);
}
